import { useState, useEffect, useRef } from 'react';
import $ from 'jquery';
import 'bootstrap-datepicker';
import 'bootstrap-datepicker/dist/css/bootstrap-datepicker.standalone.css';
import { useApp } from '../AppContext';
import TopNav from '../components/TopNav';
import Chip from '../components/Chip';
import { ordersApi } from '../services/api';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const PAGE_SIZE = 10;

function pageRange(current, total) {
  const delta = 2;
  const start = Math.max(1, current - delta);
  const end   = Math.min(total, current + delta);
  const range = [];
  for (let i = start; i <= end; i++) range.push(i);
  return range;
}

const STATUS_OPTIONS = [
  { key: 'Verification Completed', value: 'Completed' },
  { key: 'Check in Progress', value: 'In-Progress' },
  { key: 'Need Information', value: 'Need Information' },
  { key: 'CE Awaited', value: 'Ceawaited' },
  { key: 'Invited Candidates', value: 'InvitedCandidates' },
  { key: 'Invited Cancelled', value: 'Invite Cancelled' },
];

// "06/03/2026" (MM/dd/yyyy from API) → "2026-06-03" (YYYY-MM-DD for date input)
function apiToInput(d) {
  if (!d) return '';
  const [mm, dd, yyyy] = d.split('/');
  return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
}

// "2026-06-03" (YYYY-MM-DD from date input) → "03-Jun-2026" (dd-MMM-yyyy for API)
function inputToApi(d) {
  if (!d) return null;
  const [yyyy, mm, dd] = d.split('-');
  return `${dd}-${MONTHS[parseInt(mm, 10) - 1]}-${yyyy}`;
}

// "2026-06-03" → "03-Jun-2026" for display
function formatDisplay(d) {
  if (!d) return '';
  const [yyyy, mm, dd] = d.split('-');
  return `${dd}-${MONTHS[parseInt(mm, 10) - 1]}-${yyyy}`;
}

function toIso(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Text input showing dd-MMM-yyyy with a bootstrap-datepicker calendar
function DateField({ value, onChange, placeholder = 'dd-MMM-yyyy' }) {
  const ref = useRef(null);

  useEffect(() => {
    const $input = $(ref.current);
    $input.datepicker({
      format: 'dd-M-yyyy',
      autoclose: true,
      todayHighlight: true,
      orientation: 'bottom auto',
    });
    $input.on('changeDate', e => onChange(toIso(e.date)));
    return () => { $input.datepicker('destroy'); $input.off('changeDate'); };
  }, []);

  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <input
        ref={ref}
        className="fsel"
        type="text"
        readOnly
        defaultValue={formatDisplay(value)}
        placeholder={placeholder}
        style={{ cursor: 'pointer', paddingRight: '28px', minWidth: '130px' }}
      />
      <span style={{ position: 'absolute', right: '8px', cursor: 'pointer', color: 'var(--g400)', pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      </span>
    </div>
  );
}

export default function Orders({ onNavigate, statusFilter: initialStatusFilter }) {
  const [orders, setOrders] = useState([]);
  const [projectList, setProjectList] = useState([]);
  const [projectFilter, setProjectFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter || '');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [appliedStatusFilter, setAppliedStatusFilter] = useState(initialStatusFilter || '');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [reminderDrawer, setReminderDrawer] = useState(false);
  const [reminderStatuses, setReminderStatuses] = useState({});
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reportDownloading, setReportDownloading] = useState(null);

  const { addToast } = useApp();

  useEffect(() => { setSelectedIds(new Set()); }, [appliedStatusFilter]);

  function fetchOrders(body) {
    setLoading(true);
    console.log(body);
    ordersApi.myOrders(body)
      .then(res => {
        if (res?.projectlist) setProjectList(res.projectlist);
        if (res?.ds?.ds) setOrders(res.ds.ds);
        if (res?.projectid) setProjectFilter(String(res.projectid));
        if (res?.from) setFromDate(apiToInput(res.from));
        if (res?.to) setToDate(apiToInput(res.to));
        setPage(1);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchOrders(initialStatusFilter ? { status: initialStatusFilter } : {});
  }, []);

  function applyFilters() {
    setAppliedStatusFilter(statusFilter);
    fetchOrders({
      projectid: projectFilter ? Number(projectFilter) : null,
      status: statusFilter || null,
      from: inputToApi(fromDate),
      to: inputToApi(toDate),
    });
  }

  function clearFilters() {
    setProjectFilter('');
    setStatusFilter('');
    setFromDate('');
    setToDate('');
    setSearch('');
    setAppliedStatusFilter('');
    fetchOrders({});
  }

  async function handleReportDownload(profileNumber) {
    if (reportDownloading === profileNumber) return;
    setReportDownloading(profileNumber);
    try {
      const { blob, filename } = await ordersApi.downloadBGCReport(profileNumber);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      addToast(`Report download failed: ${e.message}`, 'error');
    } finally {
      setReportDownloading(null);
    }
  }

  const filtered = search
    ? orders.filter(o =>
        o.profilename?.toLowerCase().includes(search.toLowerCase()) ||
        o.profile_number?.toLowerCase().includes(search.toLowerCase()) ||
        o.email_address?.toLowerCase().includes(search.toLowerCase())
      )
    : orders;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageItems  = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const showFrom   = filtered.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const showTo     = Math.min(safePage * PAGE_SIZE, filtered.length);

  function goTo(p) { setPage(Math.max(1, Math.min(p, totalPages))); }

  const isInvitedFilter = appliedStatusFilter === 'InvitedCandidates';
  const allOnPageSelected = isInvitedFilter && pageItems.length > 0 && pageItems.every(o => selectedIds.has(o.profile_id));

  function toggleSelect(profileId) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(profileId)) next.delete(profileId);
      else next.add(profileId);
      return next;
    });
  }

  function toggleAllOnPage(checked) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      pageItems.forEach(o => {
        if (checked) next.add(o.profile_id);
        else next.delete(o.profile_id);
      });
      return next;
    });
  }

  function openReminderDrawer() {
    if (selectedIds.size === 0) {
      addToast('Please select any profile.', 'error');
      return;
    }
    const statuses = {};
    filtered.forEach(o => {
      if (selectedIds.has(o.profile_id)) {
        statuses[o.profile_id] = { name: o.profilename, status: 'In-progress' };
      }
    });
    setReminderStatuses(statuses);
    setReminderDrawer(true);
  }

  async function handleExportExcel() {
    try {
      const blob = await ordersApi.exportToExcel({
        ProjectId:    projectFilter || '',
        ddlStatus:    appliedStatusFilter || '',
        From:         inputToApi(fromDate) || '',
        To:           inputToApi(toDate)   || '',
        chkOpenEmp:   false,
        chkVerifyEmp: false,
      });
      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href     = url;
      a.download = 'OrderExcel.xls';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      addToast('Failed to export Excel. Please try again.', 'error');
    }
  }

  async function sendReminders() {
    setSendingReminder(true);
    const ids = Object.keys(reminderStatuses);
    await Promise.all(ids.map(async (profileId) => {
      try {
        const result = await ordersApi.sentReminder(profileId);
        setReminderStatuses(prev => ({
          ...prev,
          [profileId]: { ...prev[profileId], status: result.ok ? 'Sent' : 'Failed' },
        }));
      } catch {
        setReminderStatuses(prev => ({
          ...prev,
          [profileId]: { ...prev[profileId], status: 'Failed' },
        }));
      }
    }));
    setSendingReminder(false);
  }

  return (
    <>
      <TopNav currentPage="orders" onNavigate={onNavigate} />
      <div className="pg">
        <div className="phd">
          <div><div className="ptitle">My Orders</div><div className="psub">All background verification orders for your account</div></div>
          <div className="flex g8">
            {isInvitedFilter && (
              <button className="btn bout" onClick={openReminderDrawer}>✉ Send Reminder</button>
            )}
            <button className="btn bout" onClick={handleExportExcel}>⬇ Export Excel</button>
            <button className="btn bout" onClick={() => onNavigate('new-order')}>+ New Order</button>
          </div>
        </div>

        {/* Filters */}
        <div className="card mb16">
          <div className="fbar" style={{ flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
            <div className="sw" style={{ flex: '1 1 180px', minWidth: 0 }}>
              <span className="sico">🔍</span>
              <input className="sinp" placeholder="Search by name, ref no., email…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="fsel" style={{ maxWidth: '180px' }} value={projectFilter} onChange={e => setProjectFilter(e.target.value)}>
              <option value="">All Projects</option>
              {projectList.map(p => (
                <option key={p.value} value={p.value}>{p.text}</option>
              ))}
            </select>
            <select className="fsel" style={{ maxWidth: '160px' }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="All">All</option>
              {STATUS_OPTIONS.map(s => (
                <option key={s.value} value={s.value}>{s.key}</option>
              ))}
            </select>
            <DateField value={fromDate} onChange={setFromDate} placeholder="From" />
            <DateField value={toDate} onChange={setToDate} placeholder="To" />
            <button className="btn bpri bsm" onClick={applyFilters}>Apply</button>
            <button className="btn bghost bsm" onClick={clearFilters}>Clear</button>
          </div>
        </div>

        {/* Table */}
        <div className="card">
          <div className="twrap">
            <table>
              <thead>
                <tr>
                  {isInvitedFilter && <th style={{ width: 36 }}><input type="checkbox" checked={allOnPageSelected} onChange={e => toggleAllOnPage(e.target.checked)} /></th>}
                  <th>Ref No.</th>
                  <th>Candidate</th>
                  <th>Emp. ID</th>
                  <th>Package</th>
                  <th>Initiation / Invited Date</th>
                  <th>Status</th>
                  {/* <th>Credits</th> */}
                  <th>Report</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={isInvitedFilter ? 9 : 8} style={{ textAlign: 'center', padding: '24px', color: 'var(--g400)' }}>Loading…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={isInvitedFilter ? 9 : 8} style={{ textAlign: 'center', padding: '24px', color: 'var(--g400)' }}>No orders found</td></tr>
                )}
                {!loading && pageItems.map((o, i) => (
                  <tr key={o.profile_number ?? i}>
                    {isInvitedFilter && <td><input type="checkbox" checked={selectedIds.has(o.profile_id)} onChange={() => toggleSelect(o.profile_id)} /></td>}
                    <td className="tdmono">{o.profile_number}</td>
                    <td>
                      <div className="tdl" onClick={() => onNavigate('order-detail', { orderId: o.profile_id, profileStatus: o.profilestatus })}>{o.profilename}</div>
                      <div className="tdm">{o.email_address}</div>
                    </td>
                    <td className="tdmono">{o.employment_number}</td>
                    <td className="tdsm">{o.profile_type}</td>
                    <td className="tdsm">
                      {o.report_initiation_date}
                      {o.candidate_invited_on && (<><br /> <div className="tdm">Invited On [{o.candidate_invited_on}]</div></>)}
                    </td>
                    <td><Chip label={o.profilestatus} /></td>
                    {/* <td>₹0 (pending)</td> */}
                    <td>{o.report_completion_date ? <button className="btn bout bsm" onClick={() => handleReportDownload(o.profile_number)} disabled={reportDownloading === o.profile_number}>{reportDownloading === o.profile_number ? <span className="spinner" style={{ width: 11, height: 11 }} /> : '⬇ PDF'}</button> : <span className="tdsm">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex jb aic mt16">
            <div className="ts tg">Showing {showFrom}–{showTo} of {filtered.length} order{filtered.length !== 1 ? 's' : ''}</div>
            {totalPages > 1 && (
              <div className="pag">
                <button className="pgbtn" onClick={() => goTo(safePage - 1)} disabled={safePage === 1}>‹</button>
                {safePage > 3 && (
                  <>
                    <button className="pgbtn" onClick={() => goTo(1)}>1</button>
                    {safePage > 4 && <span className="pgbtn" style={{ border: 'none', cursor: 'default' }}>…</span>}
                  </>
                )}
                {pageRange(safePage, totalPages).map(p => (
                  <button key={p} className={`pgbtn${p === safePage ? ' on' : ''}`} onClick={() => goTo(p)}>{p}</button>
                ))}
                {safePage < totalPages - 2 && (
                  <>
                    {safePage < totalPages - 3 && <span className="pgbtn" style={{ border: 'none', cursor: 'default' }}>…</span>}
                    <button className="pgbtn" onClick={() => goTo(totalPages)}>{totalPages}</button>
                  </>
                )}
                <button className="pgbtn" onClick={() => goTo(safePage + 1)} disabled={safePage === totalPages}>›</button>
              </div>
            )}
          </div>
        </div>
      </div>


      {reminderDrawer && (
        <div className="dover" onClick={() => setReminderDrawer(false)}>
          <div className="drawer" onClick={e => e.stopPropagation()}>
            <div className="dhead">
              <div className="dtitle">Reminder Status</div>
              <button className="dclose" onClick={() => setReminderDrawer(false)}>✕</button>
            </div>
            <div className="dbody">
              <div className="twrap">
                <table>
                  <thead>
                    <tr>
                      <th>Employee Name</th>
                      <th style={{ width: 150, whiteSpace: 'nowrap' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(reminderStatuses).map(([id, info]) => (
                      <tr key={id}>
                        <td>{info.name}</td>
                        <td><Chip label={info.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex g8 mt16" style={{ justifyContent: 'flex-end' }}>
                <button className="btn bghost" onClick={() => setReminderDrawer(false)}>Cancel</button>
                <button className="btn bpri" onClick={sendReminders} disabled={sendingReminder}>
                  {sendingReminder ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Sending…</> : 'Send Reminder'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
